import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Typography,
  Avatar,
  Tooltip,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Quiz as QuizIcon,
  Message as MessageIcon,
  Assessment as AssessmentIcon,
  WorkspacePremium as CertificateIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Bookmark as BookmarkIcon,
  CalendarToday as CalendarIcon,
  MenuBook as MenuBookIcon,
  BugReport as BugReportIcon,
  Group as GroupIcon,
  Business as BusinessIcon,
  Layers as LayersIcon,
  EventAvailable as AttendanceIcon,
  Grading as GradebookIcon,
  Leaderboard as RankingsIcon,
  SupportAgent as AdvisingIcon,
  SupervisorAccount as SupervisorAccountIcon,
  EmojiEmotions as EmojiEmotionsIcon,
  Hub as HubIcon,
  AutoGraph as AutoGraphIcon,
  HowToReg as EnrollmentIcon,
  TravelExplore as ResearchIcon,
  EmojiEvents as RankingIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import brandLogo from '../../assets/images/logos/transparent_logo.png';

  
const drawerWidth = 260;
const collapsedDrawerWidth = 72;

// ── Self-retiring nav badges ────────────────────────────────────────────────
// A plain string (e.g. `badge: 'AI'`) is a permanent feature-category tag and
// renders as-is, unchanged, forever.
//
// An object badge — `badge: { since: '2026-09-01' }` — is an ANNOUNCEMENT tag
// for a just-shipped feature, rendered via the translated nav.badgeNew string.
// It retires itself with no further upkeep: it disappears once the item has
// been clicked NEW_BADGE_CLICKS_TO_DISMISS times (the admin has now actually
// found the feature), or once NEW_BADGE_TTL_DAYS have passed since `since`
// (nobody should still be calling a month-old feature "new"), whichever comes
// first. Dismissal is remembered per-browser via localStorage. To flag a
// future feature, just add `badge: { since: '<launch date>' }` to its nav
// item — no other wiring needed.
const NEW_BADGE_TTL_DAYS = 30;
const NEW_BADGE_CLICKS_TO_DISMISS = 2;

const NewBadgeChip = ({ id, since, clicks }) => {
  const { t } = useTranslation('nav');
  const storageKey = `vitrine_badge_clicks_${id}`;
  const [seenClicks, setSeenClicks] = useState(() => {
    try { return Number(localStorage.getItem(storageKey)) || 0; } catch { return 0; }
  });

  useEffect(() => {
    if (!clicks) return; // 0/undefined = initial render, not an actual click yet
    setSeenClicks(clicks);
    try { localStorage.setItem(storageKey, String(clicks)); } catch { /* private mode */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clicks]);

  const expired = since ? (Date.now() - new Date(since).getTime()) / 86_400_000 > NEW_BADGE_TTL_DAYS : false;
  const fading = seenClicks > 0 && seenClicks < NEW_BADGE_CLICKS_TO_DISMISS;
  if (expired || seenClicks >= NEW_BADGE_CLICKS_TO_DISMISS) return null;

  return (
    <Chip
      label={t('badgeNew')}
      size="small"
      sx={{
        height: 18,
        fontSize: '0.55rem',
        fontWeight: 700,
        bgcolor: 'primary.main',
        color: '#fff',
        ml: 0.5,
        animation: fading ? 'none' : 'softPulse 2.4s ease-in-out infinite',
        opacity: fading ? 0.45 : 1,
        transform: fading ? 'scale(0.85)' : 'scale(1)',
        transition: 'opacity 0.35s ease, transform 0.35s ease',
      }}
    />
  );
};

const Sidebar = ({ open, onToggle }) => {

  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('nav');

  const user = useAuthStore((state) => state.user);
  const university = useAuthStore((state) => state.university);
  const logout = useAuthStore((state) => state.logout);

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Avatar image: super admins see the platform brand logo; every university-
  // associated user (university_admin, teacher, student) sees their university's
  // logo (resolving /uploads paths against the API origin). It is ALWAYS an
  // image — when no university logo is set we fall back to the platform brand
  // logo rather than the user's initials.
  const isSuperAdmin = user?.role === 'super_admin';
  const resolveLogo = (logo) =>
    !logo ? null : logo.startsWith('http') ? logo : `${window.location.origin}${logo}`;
  const brandImgSrc = isSuperAdmin
    ? brandLogo
    : (resolveLogo(university?.logo) || brandLogo);
  // Last-resort glyph only if even the brand image fails to load.
  const brandFallback = university?.name?.charAt(0) || user?.firstName?.charAt(0) || '?';
  // If the university logo URL fails to load (e.g. the file is missing on the
  // server), fall back to the platform brand logo so an image always shows.
  const onLogoError = (e) => {
    if (e?.currentTarget && e.currentTarget.src !== brandLogo) {
      e.currentTarget.onerror = null;
      e.currentTarget.src = brandLogo;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Navigate, then collapse the overlay on mobile so the destination page is
  // visible immediately instead of sitting behind the open drawer.
  const [navClicks, setNavClicks] = useState({});
  const handleNavigate = (path, itemId) => {
    navigate(path);
    if (itemId) setNavClicks((c) => ({ ...c, [itemId]: (c[itemId] || 0) + 1 }));
    if (isMobile && open) onToggle();
  };

  // Humanise a role for display. `.replace('_', ' ')` only swaps the FIRST
  // underscore, so head_of_faculty rendered as "Head Of_faculty" on every Dean's
  // profile card.
  // Role names are translated, not derived from the enum. Title-casing
  // `head_of_faculty` mechanically produced "Head Of Faculty" — wrong in
  // English and still English in every other language, on every page this
  // chip appears. The split/capitalise remains only as a fallback for a role
  // that has no string yet, so a new enum value degrades instead of vanishing.
  const roleLabel = (role) =>
    t(`roles.${role}`, {
      defaultValue: String(role || '')
        .split('_')
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' '),
    });

  const getNavItems = () => {
    const role = user?.role;
    const common = [
      { 
        id: 'dashboard',
        label: t('dashboard'), 
        icon: <DashboardIcon />, 
        path: '/dashboard',
        roles: ['student', 'teacher', 'university_admin', 'head_of_faculty', 'super_admin', 'admin']
      },
      {
        // The /courses page is the staff course catalogue (admin/teacher).
        // Students don't have access to it — they use "My Courses" + "Enroll" —
        // so this item is omitted for them to avoid a dead-end link.
        id: 'courses',
        label: t('courses'),
        icon: <SchoolIcon />,
        path: '/courses',
        roles: ['teacher', 'university_admin', 'head_of_faculty', 'super_admin', 'admin']
      }
    ];

    const studentItems = [
      {
        id: 'my-courses',
        label: t('myCourses'),
        icon: <MenuBookIcon />,
        path: '/my-courses',
        section: 'sectionCoursework',
        roles: ['student']
      },
      {
        id: 'assignments',
        label: t('assignments'),
        icon: <AssignmentIcon />,
        path: '/assignments',
        section: 'sectionCoursework',
        roles: ['student']
      },
      {
        id: 'quizzes',
        label: t('quizzes'),
        icon: <QuizIcon />,
        path: '/quizzes',
        section: 'sectionCoursework',
        roles: ['student']
      },
      { 
        id: 'grades',
        label: t('grades'), 
        icon: <AssessmentIcon />, 
        path: '/grades',
        section: 'sectionCoursework',
        roles: ['student']
      },
      {
        id: 'enroll',
        label: t('enroll'),
        icon: <EnrollmentIcon />,
        path: '/enroll',
        section: 'sectionLearning',
        roles: ['student']
      },
      {
        id: 'learning-hub',
        label: t('learningHub'),
        icon: <HubIcon />,
        path: '/learning-hub',
        section: 'sectionLearning',
        roles: ['student'],
        badge: 'AI',
      },
      {
        id: 'discussions',
        label: t('discussions'),
        icon: <MessageIcon />,
        path: '/discussions',
        section: 'sectionLearning',
        roles: ['student']
      },
      {
        id: 'research-student',
        label: t('research'),
        icon: <ResearchIcon />,
        path: '/research',
        section: 'sectionLearning',
        roles: ['student'],
      },
      {
        id: 'my-transcript',
        label: t('transcript'),
        icon: <SchoolIcon />,
        path: '/transcript',
        section: 'sectionMyRecord',
        roles: ['student']
      },
      {
        id: 'certificates',
        label: t('certificates'),
        icon: <CertificateIcon />,
        path: '/certificates',
        section: 'sectionMyRecord',
        roles: ['student']
      },
      {
        id: 'student-spotlight',
        label: t('spotlight'),
        icon: <RankingIcon />,
        path: '/spotlight',
        section: 'sectionMyRecord',
        roles: ['student']
      }
    ];

    const teacherItems = [
      { 
        id: 'teaching-courses',
        label: t('teaching'), 
        icon: <SchoolIcon />, 
        path: '/teaching',
        section: 'sectionTeaching',
        roles: ['teacher']
      },
      { 
        id: 'create-content',
        label: t('createContent'),
        icon: <AssignmentIcon />, 
        path: '/content/create',
        section: 'sectionTeaching',
        roles: ['teacher']
      },
      {
        id: 'manage-assignments',
        label: t('assignments'),
        icon: <AssignmentIcon />,
        path: '/assignments',
        section: 'sectionAssessment',
        roles: ['teacher']
      },
      {
        id: 'manage-quizzes',
        label: t('quizzes'),
        icon: <QuizIcon />,
        path: '/quizzes',
        section: 'sectionAssessment',
        roles: ['teacher']
      },
      {
        id: 'gradebook',
        label: t('gradebook'),
        icon: <AssessmentIcon />,
        path: '/gradebook',
        section: 'sectionAssessment',
        roles: ['teacher']
      },
      {
        id: 'attendance',
        label: t('attendance'),
        icon: <EnrollmentIcon />,
        path: '/attendance',
        section: 'sectionClassroom',
        roles: ['teacher']
      },
      {
        // Teachers post and moderate here too. Nav items are assembled from
        // per-role arrays, so the entry in studentItems never reaches a
        // teacher — this needs its own entry, not a wider `roles` list.
        id: 'discussions-teacher',
        label: t('discussions'),
        icon: <MessageIcon />,
        path: '/discussions',
        section: 'sectionClassroom',
        roles: ['teacher']
      },
      {
        id: 'advisees-teacher',
        label: t('advisees'),
        icon: <SupervisorAccountIcon />,
        path: '/advisees',
        section: 'sectionClassroom',
        roles: ['teacher']
      },
      {
        id: 'rankings-teacher',
        label: t('rankings'),
        icon: <RankingIcon />,
        path: '/rankings',
        section: 'sectionMore',
        roles: ['teacher'],
        badge: { since: '2026-08-30' },
      },
      {
        id: 'research-teacher',
        label: t('research'),
        icon: <ResearchIcon />,
        path: '/research',
        section: 'sectionMore',
        roles: ['teacher']
      },
      {
        // A teacher could already reach /spotlight by URL but had no way in —
        // the "reachable but unlinked" pattern. They are staff and see the
        // averages, so the board belongs in their nav too.
        id: 'teacher-spotlight',
        label: t('spotlight'),
        icon: <RankingIcon />,
        path: '/spotlight',
        section: 'sectionMore',
        roles: ['teacher']
      }
    ];

    // A Head of Faculty (Dean) runs one faculty, not the institution: their
    // departments, the people inside them, and their tiers. Every destination
    // here is server-scoped by utils/facultyScope, so the nav never promises
    // more reach than the API will grant.
    // A Dean's nav runs to eight entries, which is a wall as a flat list — so it
    // is grouped the way the job actually splits: the faculty they RUN, then the
    // faculty they WATCH. `section` is optional and only this array sets it, so
    // every other role's nav renders exactly as before.
    const facultyItems = [
      {
        id: 'faculty-departments',
        label: t('departments'),
        icon: <BusinessIcon />,
        path: '/departments',
        section: 'sectionMyFaculty',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-tiers',
        label: t('tiers'),
        icon: <LayersIcon />,
        path: '/tiers',
        section: 'sectionMyFaculty',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-people',
        label: t('users'),
        icon: <PeopleIcon />,
        path: '/users',
        section: 'sectionMyFaculty',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-academic-periods',
        label: t('academicPeriods'),
        icon: <SchoolIcon />,
        path: '/academic-periods',
        section: 'sectionAcademic',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-gradebook',
        label: t('gradebook'),
        icon: <GradebookIcon />,
        path: '/gradebook',
        section: 'sectionAcademic',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-rankings',
        label: t('rankings'),
        icon: <RankingsIcon />,
        path: '/rankings',
        section: 'sectionAcademic',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-certificate-design',
        label: t('certificateDesign'),
        icon: <CertificateIcon />,
        path: '/certificate-design',
        section: 'sectionAcademic',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-attendance',
        label: t('attendance'),
        icon: <AttendanceIcon />,
        path: '/attendance-reports',
        section: 'sectionOversight',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-advising',
        label: t('advising'),
        icon: <AdvisingIcon />,
        path: '/faculty-advising',
        section: 'sectionOversight',
        roles: ['head_of_faculty']
      },
      {
        id: 'faculty-spotlight',
        label: t('spotlight'),
        icon: <RankingIcon />,
        path: '/spotlight',
        section: 'sectionOversight',
        roles: ['head_of_faculty']
      }
    ];

    const adminItems = [
      {
        id: 'users',
        label: t('users'),
        icon: <PeopleIcon />,
        path: '/users',
        section: 'sectionPeople',
        roles: ['university_admin', 'super_admin', 'admin']
      },
      {
        id: 'enrollment',
        label: t('enrollment'),
        icon: <EnrollmentIcon />,
        path: '/enrollment',
        section: 'sectionPeople',
        roles: ['university_admin', 'super_admin'],
      },
      {
        id: 'advisees-admin',
        label: t('advisees'),
        icon: <SupervisorAccountIcon />,
        path: '/advisees',
        section: 'sectionPeople',
        roles: ['university_admin', 'super_admin'],
      },
      { 
        id: 'departments',
        label: t('departments'), 
        icon: <GroupIcon />, 
        path: '/departments',
        section: 'sectionAcademic',
        roles: ['university_admin', 'super_admin']
      },
      {
        // Closing a period is an institution-wide act for an admin; a Dean
        // reaches the same page through facultyItems, narrowed to their faculty.
        id: 'admin-academic-periods',
        label: t('academicPeriods'),
        icon: <SchoolIcon />,
        path: '/academic-periods',
        section: 'sectionAcademic',
        roles: ['university_admin', 'super_admin']
      },
      {
        id: 'attendance-reports',
        label: t('attendance'),
        icon: <AutoGraphIcon />,
        path: '/attendance-reports',
        section: 'sectionAcademic',
        roles: ['university_admin', 'super_admin'],
      },
      {
        // University admins edit their OWN institution here. Super admins use
        // the platform-wide Universities list instead.
        id: 'institution',
        label: t('institution'),
        icon: <BusinessIcon />,
        path: '/my-university',
        section: 'sectionInstitution',
        roles: ['university_admin']
      },
      {
        id: 'certificate-design',
        label: t('certificateDesign'),
        icon: <CertificateIcon />,
        path: '/certificate-design',
        section: 'sectionInstitution',
        roles: ['university_admin', 'super_admin']
      },
      {
        id: 'emoji-studio',
        label: t('emojiStudio', 'Emoji Studio'),
        icon: <EmojiEmotionsIcon />,
        path: '/emoji-studio',
        section: 'sectionInstitution',
        roles: ['university_admin', 'super_admin'],
      },
      {
        id: 'analytics',
        label: t('analytics'),
        icon: <AssessmentIcon />,
        path: '/analytics',
        // Platform-wide analytics — super admin only. University admins get
        // their scoped analytics in the dashboard's Analytics tab.
        section: 'sectionInsight',
        roles: ['super_admin']
      },
      {
        id: 'rankings-admin',
        label: t('rankings'),
        icon: <RankingIcon />,
        path: '/rankings',
        section: 'sectionInsight',
        roles: ['university_admin', 'super_admin'],
        badge: { since: '2026-08-30' },
      },
      {
        id: 'admin-spotlight',
        label: t('spotlight'),
        icon: <RankingIcon />,
        path: '/spotlight',
        section: 'sectionInsight',
        roles: ['university_admin', 'super_admin']
      },
      {
        id: 'research-admin',
        label: t('research'),
        icon: <ResearchIcon />,
        path: '/research',
        section: 'sectionInsight',
        roles: ['university_admin'],
      }
    ];

    const superAdminItems = [
      { 
        id: 'universities',
        label: t('universities'), 
        icon: <BusinessIcon />, 
        path: '/universities',
        roles: ['super_admin']
      },
      {
        id: 'admin-users',
        label: t('admins'),
        icon: <SupervisorAccountIcon />,
        path: '/admins',
        roles: ['super_admin']
      },
      {
        id: 'system-settings',
        label: t('systemSettings'),
        icon: <SettingsIcon />,
        path: '/system-settings',
        roles: ['super_admin']
      },
      {
        id: 'error-monitoring',
        label: t('errorMonitoring', 'Error Monitoring'),
        icon: <BugReportIcon />,
        path: '/error-monitoring',
        roles: ['super_admin']
      },
      {
        id: 'super-admin-guide',
        label: t('guide', 'Guide'),
        icon: <MenuBookIcon />,
        path: '/guide',
        roles: ['super_admin']
      }
    ];

    let items = [...common];

    if (role === 'student') {
      items = [...items, ...studentItems];
    } else if (role === 'teacher') {
      items = [...items, ...teacherItems];
    } else if (role === 'head_of_faculty') {
      items = [...items, ...facultyItems];
    } else if (role === 'university_admin' || role === 'admin') {
      items = [...items, ...adminItems];
    } else if (role === 'super_admin') {
      items = [...items, ...adminItems, ...superAdminItems];
    }

    // Add common items at the end
    items.push(
      { 
        id: 'calendar',
        label: t('calendar'), 
        icon: <CalendarIcon />, 
        path: '/calendar',
        roles: ['student', 'teacher', 'university_admin', 'head_of_faculty', 'super_admin']
      },
      { 
        id: 'bookmarks',
        label: t('bookmarks'), 
        icon: <BookmarkIcon />, 
        path: '/bookmarks',
        roles: ['student', 'teacher']
      }
    );

    return items.filter(item => item.roles.includes(role));
  };

  const navItems = getNavItems();

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo Area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          p: open ? 2 : 1,
          minHeight: 64
        }}
      >
        {open ? (
          <>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Vitrine LMS
            </Typography>
            {/* The chevron's direction is the only clue to what it does, and
                that flips with text direction — so it needs saying. */}
            <Tooltip title={t('tooltips.collapseMenu', { ns: 'common' })}>
              <IconButton onClick={onToggle} aria-label={t('tooltips.collapseMenu', { ns: 'common' })}>
                {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title={t('tooltips.expandMenu', { ns: 'common' })} placement="right">
            <IconButton onClick={onToggle} aria-label={t('tooltips.expandMenu', { ns: 'common' })}>
              {theme.direction === 'ltr' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider />

      {/* User Info */}
      {open && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar
            src={brandImgSrc || undefined}
            imgProps={{ style: { objectFit: 'cover' }, onError: onLogoError }}
            sx={{ width: 72, height: 72, mr: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
          >
            {brandFallback}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {user?.email}
            </Typography>
            <Chip 
              label={roleLabel(user?.role)} 
              size="small"
              color="primary"
              variant="outlined"
              // No textTransform: the role name is already correctly cased per
              // language, and `capitalize` would re-break it ("Head Of Faculty",
              // "Administrateur D'université").
              sx={{ mt: 0.5 }}
            />
          </Box>
        </Box>
      )}

      {!open && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Tooltip title={isSuperAdmin ? 'Vitrine LMS' : (university?.name || `${user?.firstName} ${user?.lastName}`)} placement="right">
            <Avatar
              src={brandImgSrc || undefined}
              imgProps={{ style: { objectFit: 'cover' }, onError: onLogoError }}
              sx={{ width: 60, height: 60, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
            >
              {brandFallback}
            </Avatar>
          </Tooltip>
        </Box>
      )}

      <Divider />

      {/* Navigation Items */}
      <List sx={{ flex: 1, px: open ? 1 : 0 }}>
        {navItems.map((item, i) => (
          <React.Fragment key={item.id}>
          {/* A section header, emitted only when the group CHANGES — so any nav
              array that sets no `section` renders exactly as it always has.
              Hidden while the drawer is collapsed, where there is no room for a
              label and the icons carry the meaning on their own. */}
          {open && item.section && item.section !== navItems[i - 1]?.section && (
            <Typography
              variant="caption"
              sx={{
                display: 'block', px: 2.5, pt: i === 0 ? 0 : 2, pb: 0.5,
                textTransform: 'uppercase', letterSpacing: '.08em',
                fontWeight: 700, color: 'text.disabled', fontSize: '0.66rem',
              }}
            >
              {t(item.section)}
            </Typography>
          )}
          <ListItem disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={{
                position: 'relative',
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                borderRadius: 1.5,
                mb: 0.5,
                overflow: 'hidden',
                bgcolor: location.pathname === item.path ? 'primary.light' : 'transparent',
                color: location.pathname === item.path ? 'primary.contrastText' : 'inherit',
                // Springy, tactile motion.
                transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease',
                '&:hover': {
                  bgcolor: location.pathname === item.path ? 'primary.light' : 'action.hover',
                  transform: open ? 'translateX(6px)' : 'scale(1.08)',
                },
                '&:hover .MuiListItemIcon-root': { transform: 'scale(1.18)' },
                '&:active': { transform: 'scale(0.96)' },
                // Animated active accent bar on the left edge.
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: location.pathname === item.path ? 'translateY(-50%) scaleY(1)' : 'translateY(-50%) scaleY(0)',
                  height: '60%',
                  width: 4,
                  borderRadius: 4,
                  bgcolor: 'primary.main',
                  transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                },
              }}
              onClick={() => handleNavigate(item.path, item.id)}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                  transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  color: location.pathname === item.path ? 'primary.contrastText' : 'text.primary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                sx={{
                  opacity: open ? 1 : 0,
                  whiteSpace: 'nowrap'
                }}
              />
              {open && item.badge && (
                typeof item.badge === 'object'
                  ? (
                    <NewBadgeChip
                      id={item.id}
                      since={item.badge.since}
                      clicks={navClicks[item.id] || 0}
                    />
                  )
                  : (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.55rem',
                        fontWeight: 700,
                        bgcolor: 'primary.main',
                        color: '#fff',
                        ml: 0.5,
                        animation: 'softPulse 2.4s ease-in-out infinite',
                      }}
                    />
                  )
              )}
              {!open && (
                <Tooltip title={item.label} placement="right">
                  <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }} />
                </Tooltip>
              )}
            </ListItemButton>
          </ListItem>
          </React.Fragment>
        ))}
      </List>

      <Divider />

      {/* Footer Items */}
      <List sx={{ px: open ? 1 : 0 }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              borderRadius: 1,
            }}
            onClick={() => handleNavigate('/settings')}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary={t('settings')} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              borderRadius: 1,
            }}
            onClick={() => handleNavigate('/help')}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
              }}
            >
              <HelpIcon />
            </ListItemIcon>
            <ListItemText primary={t('help')} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              borderRadius: 1,
              color: 'error.main',
            }}
            onClick={handleLogout}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 2 : 'auto',
                justifyContent: 'center',
                color: 'error.main',
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('logout')} sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { md: open ? drawerWidth : collapsedDrawerWidth },
        flexShrink: { md: 0 },
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth 
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        // Desktop Drawer
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: open ? drawerWidth : collapsedDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open={open}
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
